/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AuthService, TokenService } from '@app/auth/services';
import { APP_CONFIG, AppConfig } from '@core/models';
import { CookieStorageService } from '@core/services';
import { BehaviorSubject, Observable, catchError, filter, finalize, switchMap, take, throwError } from 'rxjs';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private cookieService: CookieStorageService,
    private authService: AuthService,
    private tokenService: TokenService,
    @Inject(APP_CONFIG) private config: AppConfig
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!request.url.includes(this.config.apiUrl)) {
      return next.handle(request);
    }

    const token = this.cookieService.get('token');
    if (token) {
      request = this.addAccessToken(request);
    }

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        // Sometimes these endpoints return http 500. Should be handled in the BE but until then temp handling here
        if (err.status === 500 && (request.url.includes('/login') || request.url.includes('/account/refresh'))) {
          this.logout();
          return throwError(() => err);
        }

        if (request.url.includes('/account/refresh')) {
          this.logout();
          return throwError(() => err);
        }

        if (err.status === 401) {
          if (request.url.includes('/login') || request.url.includes('/account/refresh')) {
            this.logout();
            return throwError(() => err);
          }

          const refreshToken = this.cookieService.get('refresh-token');

          if (refreshToken) {
            if (this.isRefreshing$.value) {
              return this.isRefreshing$.pipe(
                filter((refreshing) => !refreshing),
                switchMap(() => {
                  request = this.addAccessToken(request);
                  return next.handle(request);
                }),
                take(2)
              );
            } else {
              this.isRefreshing$.next(true);

              return this.authService.refreshToken(refreshToken).pipe(
                switchMap((res) => {
                  this.tokenService.store(res);
                  request = this.addAccessToken(request);
                  return next.handle(request);
                }),
                finalize(() => {
                  this.isRefreshing$.next(false);
                })
              );
            }
          } else {
            this.logout();
          }
        }

        return throwError(() => err);
      })
    );
  }

  private addAccessToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.cookieService.get('token');

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private logout(): void {
    this.authService.logout();
  }
}
